import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';

interface AppSettings {
  launch_date: Date | null;
  maintenance_mode: boolean;
  last_commit_sha: string;
}

class AppSettingsCache {
  private static instance: AppSettingsCache;
  private cache: AppSettings | null = null;
  private fetchPromise: Promise<AppSettings> | null = null;
  private listeners: Set<(settings: AppSettings) => void> = new Set();

  private constructor() {}

  static getInstance(): AppSettingsCache {
    if (!AppSettingsCache.instance) {
      AppSettingsCache.instance = new AppSettingsCache();
    }
    return AppSettingsCache.instance;
  }

  async getSettings(): Promise<AppSettings> {
    // Se já temos cache, retornar imediatamente
    if (this.cache) {
      return this.cache;
    }

    // Se já está fetchando, retornar a promise existente
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    // Iniciar novo fetch
    this.fetchPromise = this.fetchFromDatabase();
    return this.fetchPromise;
  }

  private async fetchFromDatabase(): Promise<AppSettings> {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('*');

      if (error) throw error;

      const settingsMap: any = {};
      const getFallbackValue = (type: string): any => {
        const fallbackValues: Record<string, any> = {
          'boolean': false,
          'timestamp': null,
          'number': 0,
          'string': 'local-dev'
        };
        return fallbackValues[type] || null;
      };

      data?.forEach((setting: any) => {
        try {
          let parsedValue = setting.value;
          
          if (parsedValue === null || parsedValue === undefined) {
            parsedValue = getFallbackValue(setting.type);
          } else if (typeof parsedValue === 'string' && parsedValue.trim() === '') {
            parsedValue = getFallbackValue(setting.type);
          } else {
            if (setting.type === 'boolean') {
              parsedValue = setting.value === true || setting.value === 'true';
            } else if (setting.type === 'timestamp') {
              parsedValue = new Date(setting.value);
            } else if (setting.type === 'number') {
              parsedValue = Number(setting.value);
            }
          }
          
          settingsMap[setting.key] = parsedValue;
        } catch (parseError) {
          debugLogger.error('[AppSettingsCache] Failed to parse setting:', {
            key: setting.key,
            error: parseError
          });
          settingsMap[setting.key] = getFallbackValue(setting.type);
        }
      });

      this.cache = {
        launch_date: settingsMap.launch_date || null,
        maintenance_mode: settingsMap.maintenance_mode || false,
        last_commit_sha: settingsMap.last_commit_sha || 'local-dev'
      };

      // Notificar listeners
      this.listeners.forEach(listener => listener(this.cache!));

      debugLogger.debug('[AppSettingsCache] Settings loaded and cached', this.cache);
      return this.cache;
    } catch (error) {
      debugLogger.error('[AppSettingsCache] Error fetching settings:', error);
      throw error;
    } finally {
      this.fetchPromise = null;
    }
  }

  subscribe(listener: (settings: AppSettings) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  invalidate(): void {
    this.cache = null;
  }
}

export const appSettingsCache = AppSettingsCache.getInstance();
